import { useState } from 'react';
import { Button, Group, Text } from '@mantine/core';
import { CheckCircle } from 'react-feather';
import { notifications } from '@mantine/notifications';
import { peerLeaderSessionPrepApi } from '../../../services/api/peerLeaderSessionPrep';
import { MarkAsCompletedRsvpInterceptModal } from './MarkAsCompletedRsvpInterceptModal';

interface MarkAsCompletedButtonProps {
  sessionId: string;
  sessionNumber: number | null;
  sessionDateLabel: string;
  cmName: string | null;
  rsvpStatus: 'unconfirmed' | 'confirmed' | 'declined';
  markedCompletedAt: string | null;
  onMarkedCompleted: () => void;
  onDeclined: () => void;
}

/**
 * AC13 — "Mark as Completed" action at the bottom of the prep page. When the
 * peer leader's RSVP is still `unconfirmed`, this button opens the intercept
 * modal before persisting; otherwise it POSTs `mark-completed` directly.
 *
 * After the prep has been marked completed at least once for this session,
 * the button switches to a static "✓ Marked as completed" indicator. The peer
 * leader can re-click to re-mark (refreshes the timestamp) — kept as a button
 * for simplicity and to mirror the coach Mark-as-Reviewed UX.
 */
export function MarkAsCompletedButton({
  sessionId,
  sessionNumber,
  sessionDateLabel,
  cmName,
  rsvpStatus,
  markedCompletedAt,
  onMarkedCompleted,
  onDeclined,
}: MarkAsCompletedButtonProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleClick = async () => {
    if (rsvpStatus === 'unconfirmed') {
      setModalOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      await peerLeaderSessionPrepApi.markCompleted(sessionId);
      onMarkedCompleted();
    } catch (err) {
      notifications.show({
        color: 'dangerRed',
        title: 'Something went wrong',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (markedCompletedAt) {
    return (
      <Group gap="xs" align="center">
        <CheckCircle
          size={20}
          color="var(--medley-color-medleygreen-7, #2D4739)"
        />
        <Text size="sm" fw={600} c="medleyGreen.9">
          Prep marked as completed
        </Text>
        <Button
          variant="subtle"
          color="gray"
          size="sm"
          loading={submitting}
          onClick={() => void handleClick()}
        >
          Mark again
        </Button>
      </Group>
    );
  }

  return (
    <>
      <Button
        color="medleyGreen"
        size="sm"
        loading={submitting}
        onClick={() => void handleClick()}
      >
        Mark as Completed
      </Button>
      <MarkAsCompletedRsvpInterceptModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        sessionId={sessionId}
        sessionNumber={sessionNumber}
        sessionDateLabel={sessionDateLabel}
        cmName={cmName}
        onMarkedCompleted={onMarkedCompleted}
        onDeclined={onDeclined}
      />
    </>
  );
}
